import type { ComboboxAbsenceType, FormDialogRef } from '@/components'
import {
  AbsenceTypeCombobox,
  Button,
  DatePicker,
  FAIcon,
  Field,
  FieldGroup,
  FieldLabel,
  FormDialog,
  Textarea,
  TimePicker,
  UserCombobox,
} from '@/components'
import { cn } from '@/lib/utils'
import UserThumbOrInitials from '@/modules/users/UserThumbOrInitials'
import { useFormWatch, useUser } from '@/utils'
import { useMutation, useQuery } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslate } from '@ur-solutions/react-hooks'
import { addDays, addHours, startOfDay, startOfHour } from 'date-fns'
import { Activity, useImperativeHandle, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'
import z from 'zod'
import { CREATE_ABSENCE_MUTATION, PATCH_ABSENCE_MUTATION } from '../mutations'
import { ABSENCE_TYPES_COMBOBOX_QUERY } from '../queries'
import { AbsencePeriodReport } from './absence-period-report'

function useAbsenceSchema() {
  const translations = useTranslate({
    userRequired: 'validation.field-required',
    absenceTypeRequired: 'validation.field-required',
  })

  return z.object({
    id: z.string(),
    user: z.string().min(1, translations.userRequired),
    absenceType: z.string().min(1, translations.absenceTypeRequired),
    datetimeStart: z.date(),
    datetimeEnd: z.date(),
    reason: z.string(),
  })
}

export type AbsenceForm = z.infer<ReturnType<typeof useAbsenceSchema>>

function transformAbsenceValues(
  values: AbsenceForm,
  selectedAbsenceType: ComboboxAbsenceType
) {
  const datetimeStart = (
    selectedAbsenceType.durationMode === 'HOURLY'
      ? startOfHour(values.datetimeStart)
      : startOfDay(values.datetimeStart)
  ).toISOString()
  const datetimeEnd = (
    selectedAbsenceType.durationMode === 'HOURLY'
      ? addHours(startOfHour(values.datetimeEnd), 1)
      : addDays(startOfDay(values.datetimeEnd), 1)
  ).toISOString()

  return {
    datetimeStart,
    datetimeEnd,
  }
}

type Absence = AbsenceForm & { id: string }

export interface AbsenceDialogRef {
  open(absence: Absence): void
}

interface Props {
  ref?: React.RefObject<AbsenceDialogRef | null>

  canAddForOthers: boolean
  hideTrigger?: boolean
}

export function AbsenceDialog({
  ref,

  canAddForOthers,
  hideTrigger = false,
}: Props) {
  const translations = useTranslate({
    headerAdd: 'absences.absence-dialog.header-add',
    headerEdit: 'absences.absence-dialog.header-edit',
    add: 'absences.absence-dialog.add',

    labels: {
      user: 'absences.absence-dialog.labels.user',
      absenceType: 'absences.absence-dialog.labels.absence-type',
      timeFrom: 'absences.absence-dialog.labels.time-from',
      timeTo: 'absences.absence-dialog.labels.time-to',
      dateFrom: 'absences.absence-dialog.labels.date-from',
      dateTo: 'absences.absence-dialog.labels.date-to',
      reason: 'absences.absence-dialog.labels.reason',
    },
  })

  const dialogRef = useRef<FormDialogRef>(null)

  const me = useUser()

  const { data: absenceTypesData } = useQuery(ABSENCE_TYPES_COMBOBOX_QUERY)

  const form = useForm<AbsenceForm>({
    resolver: zodResolver(useAbsenceSchema()),
    defaultValues: {
      id: '',
      user: canAddForOthers ? '' : me.id,
      absenceType: '',
      datetimeStart: startOfHour(new Date()),
      datetimeEnd: addHours(startOfHour(new Date()), 1),
      reason: '',
    },
    mode: 'onBlur',
  })

  useImperativeHandle(ref, () => ({
    open(absence: Absence) {
      dialogRef.current?.open()
      form.reset(absence)
    },
  }))

  const absenceId = useFormWatch(form, 'id')

  const absenceTypeId = useFormWatch(form, 'absenceType')
  const selectedAbsenceType =
    absenceTypesData?.allAbsenceTypes.find(type => type.id === absenceTypeId) ??
    null

  const userId = useFormWatch(form, 'user')

  const showTimePicker = selectedAbsenceType?.durationMode === 'HOURLY'

  const [createAbsence] = useMutation(CREATE_ABSENCE_MUTATION, {
    refetchQueries: ['UserAbsences', 'Sidebar'],
  })

  const [patchAbsence] = useMutation(PATCH_ABSENCE_MUTATION, {
    refetchQueries: ['UserAbsences', 'Sidebar'],
  })

  function userCanHaveSickChildAbsence(userId: string) {
    const user = absenceTypesData?.allUsersList.find(u => u.id === userId)
    if (!user) return false
    return !!user.childcare.numberOfChildren
  }

  function resetForm() {
    setTimeout(() => {
      form.reset()
    }, 300)
  }

  async function handleSubmit(values: AbsenceForm) {
    if (!selectedAbsenceType) return

    if (!values.id)
      await createAbsence({
        variables: {
          input: {
            user: values.user,
            absenceType: values.absenceType,
            reason: values.reason,
            ...transformAbsenceValues(values, selectedAbsenceType),
          },
        },
      })
    else
      await patchAbsence({
        variables: {
          id: values.id,
          input: {
            user: values.user,
            absenceType: values.absenceType,
            reason: values.reason,
            ...transformAbsenceValues(values, selectedAbsenceType),
          },
        },
      })

    dialogRef.current?.close()
  }

  return (
    <FormDialog
      ref={dialogRef}
      form={form}
      trigger={
        <Button className={cn('w-full', hideTrigger && 'hidden')}>
          <FAIcon icon="plus" />
          {translations.add}
        </Button>
      }
      header={!absenceId ? translations.headerAdd : translations.headerEdit}
      confirmDisabled={!selectedAbsenceType}
      noCloseOnEscape
      onClose={resetForm}
      onSubmit={handleSubmit}
    >
      <FieldGroup>
        <Activity mode={canAddForOthers ? 'visible' : 'hidden'}>
          <Controller
            name="user"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={!!fieldState.error}>
                <FieldLabel>{translations.labels.user}</FieldLabel>

                <UserCombobox
                  {...field}
                  mode="single"
                  alwaysInclude={me}
                  onChange={value => {
                    if (
                      selectedAbsenceType?.preset === 'SICK_CHILD_NO' &&
                      !userCanHaveSickChildAbsence(value)
                    )
                      form.setValue('absenceType', '')

                    field.onChange(value)
                  }}
                />
              </Field>
            )}
          />
        </Activity>

        <Activity mode={!canAddForOthers ? 'visible' : 'hidden'}>
          <Field>
            <FieldLabel>{translations.labels.user}</FieldLabel>

            <div className="flex items-center gap-4 px-6 py-3 rounded-xl border border-gray-200 bg-quaternary">
              <UserThumbOrInitials user={me} />

              <span className="text-lg font-semibold">{me.fullName}</span>
            </div>
          </Field>
        </Activity>

        <Controller
          name="absenceType"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={!!fieldState.error}>
              <FieldLabel>{translations.labels.absenceType}</FieldLabel>

              <AbsenceTypeCombobox
                {...field}
                mode="single"
                hideItem={item => {
                  if (item.preset !== 'SICK_CHILD_NO') return false
                  return !userCanHaveSickChildAbsence(userId)
                }}
              />
            </Field>
          )}
        />

        <Activity
          mode={selectedAbsenceType?.preset !== 'SIMPLE' ? 'visible' : 'hidden'}
        >
          <AbsencePeriodReport form={form} />
        </Activity>

        <Activity mode={!!selectedAbsenceType ? 'visible' : 'hidden'}>
          <Controller
            name="datetimeStart"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={!!fieldState.error}>
                <FieldLabel>
                  {showTimePicker
                    ? translations.labels.timeFrom
                    : translations.labels.dateFrom}
                </FieldLabel>

                <div className="flex items-end gap-2">
                  {showTimePicker && <TimePicker {...field} />}

                  <DatePicker
                    {...field}
                    className="w-full"
                    mode="single"
                    onChange={date => {
                      if (!date) return
                      field.onChange(date)
                    }}
                  />
                </div>
              </Field>
            )}
          />

          <Controller
            name="datetimeEnd"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={!!fieldState.error}>
                <FieldLabel>
                  {showTimePicker
                    ? translations.labels.timeTo
                    : translations.labels.dateTo}
                </FieldLabel>

                <div className="flex items-end gap-2">
                  {showTimePicker && <TimePicker {...field} />}

                  <DatePicker
                    {...field}
                    className="w-full"
                    mode="single"
                    onChange={date => {
                      if (!date) return
                      field.onChange(date)
                    }}
                  />
                </div>
              </Field>
            )}
          />

          <Controller
            name="reason"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={!!fieldState.error}>
                <FieldLabel>{translations.labels.reason}</FieldLabel>

                <Textarea {...field} rows={4} />
              </Field>
            )}
          />
        </Activity>
      </FieldGroup>
    </FormDialog>
  )
}
